import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "../../design/primitives/Alert";
import { Button } from "../../design/primitives/Button";
import styles from "./room.module.css";

export interface DeviceCheckProps {
  readonly onReady: () => void;
}

type CheckState = "pending" | "ok" | "fail";

/**
 * O resultado de cada sonda, traduzido.
 *
 * A tela montava a chave por concatenacao — `t('room.capture.' + camera)` — sobre
 * um vocabulario que ela mesma inventou: `room.capture` e o enum `CaptureState`
 * do contrato (idle, probing, active, recovering, stopped, failed) e NAO tem
 * `pending`, `ok` nem `fail`. Com `returnNull: false` o i18next devolve a propria
 * chave, entao o teste de equipamento — a primeira coisa que qualquer pessoa ve
 * ao entrar na aula — exibia literalmente "Testar camera: room.capture.pending" e
 * depois "room.capture.ok", nos tres idiomas. `fail` errava por uma letra o
 * `failed` que existe, o que mostra de onde veio o descuido.
 *
 * A tabela e explicita, e nao mais uma concatenacao: chave montada em tempo de
 * execucao nao tem como ser conferida por ninguem — nem pelo compilador, nem por
 * quem revisa, nem pelo teste — e foi exatamente assim que tres chaves
 * inexistentes chegaram a producao. `Record<CheckState, string>` obriga a tabela a
 * cobrir os tres estados: quem acrescentar um quarto nao compila.
 *
 * As tres chaves sao emprestadas de vocabularios que ja existem nos tres
 * catalogos e dizem a verdade ("Verificando", "Concluido", "Falhou"). O catalogo
 * proprio deste teste — `room.deviceCheck.*` — nao e desta fatia; ver `fronteira`.
 */
const CHECK_LABEL: Record<CheckState, string> = {
  pending: "room.capture.probing",
  ok: "alert.success",
  fail: "room.capture.failed",
};

export function DeviceCheck({ onReady }: DeviceCheckProps) {
  const { t } = useTranslation();
  const [camera, setCamera] = useState<CheckState>("pending");
  const [mic, setMic] = useState<CheckState>("pending");
  const [network, setNetwork] = useState<CheckState>("pending");

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        stream.getTracks().forEach((track) => track.stop());
        if (!cancelled) {
          setCamera("ok");
          setMic("ok");
        }
      } catch {
        if (!cancelled) {
          setCamera("fail");
          setMic("fail");
        }
      }
      if (!cancelled) setNetwork(navigator.onLine ? "ok" : "fail");
    }
    void run();
    return () => { cancelled = true; };
  }, []);

  const ready = camera === "ok" && mic === "ok" && network === "ok";

  return (
    <section aria-labelledby="device-check-heading">
      <h2 id="device-check-heading">{t("room.deviceCheckTitle")}</h2>
      <ul className={styles.checkList}>
        <li>{t("room.testCamera")}: {t(CHECK_LABEL[camera])}</li>
        <li>{t("room.testMic")}: {t(CHECK_LABEL[mic])}</li>
        <li>{t("room.testNetwork")}: {t(CHECK_LABEL[network])}</li>
      </ul>
      {!ready ? <Alert tone="warning">{t("room.deviceCheckBody")}</Alert> : null}
      <Button variant="primary" disabled={!ready} onClick={onReady}>{t("room.enterRoom")}</Button>
    </section>
  );
}
